import React, { useState, useEffect } from 'react';
import { Category } from '../../../crm/components/category/types';
import { ContactWithDetails } from '../../../crm/components/contacts/types';
import { residentApi } from '../../services/residentApi';

interface CategoryDropdownProps {
  parentType: string; // 'contact', 'phone', 'email', 'address'
  value: number | string; // Allow both number (ID) and string for backward compatibility
  onChange: (value: number) => void; // Always return number (ID)
  className?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  existingContacts?: ContactWithDetails[]; // Existing contacts to check for duplicates
  currentContactId?: number | null; // Current contact being edited (exclude from duplicate check)
}

const CategoryDropdown: React.FC<CategoryDropdownProps> = ({
  parentType,
  value,
  onChange,
  className = '',
  placeholder = 'Select...',
  required = false,
  disabled = false,
  existingContacts = [],
  currentContactId = null
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await residentApi.getCategoriesByType(parentType);
        
        // Validate response structure
        if (!response) {
          throw new Error('No response from server');
        }
        
        if (!response.data) {
          throw new Error('Invalid response format: missing data property');
        }
        
        // Ensure response.data is an array
        if (!Array.isArray(response.data)) {
          console.error('Expected array but got:', typeof response.data, response.data);
          throw new Error('Invalid response format: data is not an array');
        }
        
        // Server already filters by isActive and type-specific rules
        // Just use the data as-is
        setCategories(response.data);
        
        // If we have a selected value, check if it's in the categories
        // If not, try to fetch the category name separately (for display purposes)
        if (value && value !== 0 && value !== '') {
          const foundCategory = response.data.find((cat: Category) => cat.id === value);
          if (!foundCategory && parentType === 'contact') {
            // For contact type, if category not found, it might be Tenant
            // Try to fetch it separately or set a placeholder
            setSelectedCategoryName('Tenant'); // Will be updated if found
          } else if (foundCategory) {
            setSelectedCategoryName(foundCategory.name);
          } else {
            setSelectedCategoryName(null);
          }
        }
      } catch (err) {
        console.error(`Error fetching categories for type "${parentType}":`, err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch categories';
        setError(errorMessage);
        setCategories([]); // Set empty array on error
        setSelectedCategoryName(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [parentType, value]);

  if (loading) {
    return (
      <select className={`app-form-select ${className}`} disabled>
        <option>Loading...</option>
      </select>
    );
  }

  if (error) {
    return (
      <select className={`app-form-select ${className}`} disabled>
        <option>Error loading categories</option>
      </select>
    );
  }

  // Check if the selected value exists in categories (for display purposes)
  const selectedCategory = categories.find(cat => cat.id === value);
  const displayValue = value === 0 ? '' : value;

  // Filter out Tenant from selectable options for contact type (users cannot create Tenant contacts)
  // But keep it available for display if it's the selected value
  let selectableCategories = parentType === 'contact' 
    ? categories.filter(cat => cat.name !== 'Tenant')
    : categories;

  // For contact type, filter out Emergency categories that are already used by other contacts
  if (parentType === 'contact' && existingContacts.length > 0) {
    const emergencyNames = ['Emergency 1', 'Emergency 2', 'Emergency 3'];
    const usedEmergencyTypeIds = new Set<number>();
    
    // Find all Emergency typeIds that are already used (excluding current contact)
    existingContacts.forEach(contact => {
      // Compare IDs with type conversion to handle string/number mismatches
      const contactId = typeof contact.id === 'string' ? Number(contact.id) : contact.id;
      const currentId = typeof currentContactId === 'string' ? Number(currentContactId) : currentContactId;
      
      if (contactId !== currentId && contact.typeId) {
        const contactTypeId = typeof contact.typeId === 'string' ? Number(contact.typeId) : contact.typeId;
        const category = categories.find(cat => {
          const catId = typeof cat.id === 'string' ? Number(cat.id) : cat.id;
          return catId === contactTypeId;
        });
        if (category && emergencyNames.includes(category.name)) {
          usedEmergencyTypeIds.add(contactTypeId);
        }
      }
    });
    
    // Filter out used Emergency categories
    selectableCategories = selectableCategories.filter(cat => {
      const catId = typeof cat.id === 'string' ? Number(cat.id) : cat.id;
      const valueId = typeof value === 'string' ? Number(value) : value;
      
      // Always include the currently selected category (so editing works)
      if (catId === valueId && valueId !== 0) {
        return true;
      }
      // Exclude if it's a used Emergency category
      return !usedEmergencyTypeIds.has(catId);
    });
  }

  return (
    <select
      className={`app-form-select ${className}`}
      value={displayValue}
      onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
      required={required}
      disabled={disabled}
    >
      {(value === 0 || !required) && <option value="">Select...</option>}
      {selectableCategories.map((category) => (
        <option key={category.id} value={category.id}>
          {category.name}
        </option>
      ))}
      {/* If selected value is Tenant (filtered out) but we have it selected, show it as disabled */}
      {value !== 0 && value !== '' && selectedCategory && selectedCategory.name === 'Tenant' && (
        <option value={value} disabled>
          {selectedCategory.name}
        </option>
      )}
      {/* If selected value is not in categories list but we have a name, show it */}
      {value !== 0 && value !== '' && !selectedCategory && selectedCategoryName && (
        <option value={value} disabled>
          {selectedCategoryName}
        </option>
      )}
    </select>
  );
};

export default CategoryDropdown;

